import React from 'react';
import './CandidateEvaluationPanel.css';

function asArray(v) {
  if (v == null) return [];
  if (Array.isArray(v)) return v.map((x) => (x == null ? '' : String(x).trim())).filter(Boolean);
  if (typeof v === 'string') {
    try {
      const p = JSON.parse(v);
      return Array.isArray(p) ? asArray(p) : [];
    } catch {
      return v
        .split(/[,;\n]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return [];
}

function decisionBadgeClass(decision) {
  const d = String(decision || '');
  if (d === 'Strongly Pursue') return 'hirelane-eval-panel__badge hirelane-eval-panel__badge--strong';
  if (d === 'Pursue for Technical Interview') return 'hirelane-eval-panel__badge hirelane-eval-panel__badge--pursue';
  if (d.startsWith('Hold')) return 'hirelane-eval-panel__badge hirelane-eval-panel__badge--hold';
  if (d === 'Weak Fit') return 'hirelane-eval-panel__badge hirelane-eval-panel__badge--weak';
  if (d === 'Reject') return 'hirelane-eval-panel__badge hirelane-eval-panel__badge--reject';
  return 'hirelane-eval-panel__badge hirelane-eval-panel__badge--neutral';
}

function coreRiskBadgeClass(level) {
  const l = String(level || '');
  if (l === 'Critical') return 'hirelane-eval-panel__riskBadge hirelane-eval-panel__riskBadge--critical';
  if (l === 'High') return 'hirelane-eval-panel__riskBadge hirelane-eval-panel__riskBadge--high';
  if (l === 'Medium') return 'hirelane-eval-panel__riskBadge hirelane-eval-panel__riskBadge--medium';
  if (l === 'Low') return 'hirelane-eval-panel__riskBadge hirelane-eval-panel__riskBadge--low';
  return 'hirelane-eval-panel__riskBadge hirelane-eval-panel__riskBadge--unknown';
}

function advertisedFitBadgeClass(decision) {
  const d = String(decision || '');
  if (d === 'Strong Fit') return 'hirelane-eval-panel__advBadge hirelane-eval-panel__advBadge--strong';
  if (d === 'Fit') return 'hirelane-eval-panel__advBadge hirelane-eval-panel__advBadge--fit';
  if (d === 'Borderline') return 'hirelane-eval-panel__advBadge hirelane-eval-panel__advBadge--borderline';
  if (d === 'Weak Fit') return 'hirelane-eval-panel__advBadge hirelane-eval-panel__advBadge--weak';
  if (d === 'Reject') return 'hirelane-eval-panel__advBadge hirelane-eval-panel__advBadge--reject';
  return 'hirelane-eval-panel__advBadge hirelane-eval-panel__advBadge--neutral';
}

function CoreRoleExecutionSection({ cer, far, alt }) {
  if (!cer && !far && !alt) return null;

  const prodConcerns = cer ? asArray(cer.production_experience_concerns) : [];
  const roles = alt ? asArray(alt.recommended_roles) : [];

  return (
    <section className="hirelane-eval-panel__roleContext">
      <h3 className="hirelane-eval-panel__heading">Core role execution risk</h3>

      {cer && typeof cer === 'object' ? (
        <div className="hirelane-eval-panel__roleContextBlock">
          <div className="hirelane-eval-panel__roleContextRow">
            {cer.risk_level ? (
              <span className={coreRiskBadgeClass(cer.risk_level)}>{cer.risk_level}</span>
            ) : null}
          </div>
          {cer.assessment ? <p className="hirelane-eval-panel__roleContextProse">{cer.assessment}</p> : null}
          <dl className="hirelane-eval-panel__roleDl">
            {cer.core_stack_requirement ? (
              <>
                <dt>Core stack / responsibility</dt>
                <dd>{cer.core_stack_requirement}</dd>
              </>
            ) : null}
            {cer.candidate_core_stack_evidence ? (
              <>
                <dt>Candidate evidence</dt>
                <dd>{cer.candidate_core_stack_evidence}</dd>
              </>
            ) : null}
            {cer.mentorship_readiness ? (
              <>
                <dt>Mentorship readiness</dt>
                <dd>{cer.mentorship_readiness}</dd>
              </>
            ) : null}
            {cer.day_one_architecture_readiness ? (
              <>
                <dt>Day-one architecture readiness</dt>
                <dd>{cer.day_one_architecture_readiness}</dd>
              </>
            ) : null}
            {cer.impact_if_hired_for_this_role ? (
              <>
                <dt>Impact if hired for this role</dt>
                <dd>{cer.impact_if_hired_for_this_role}</dd>
              </>
            ) : null}
          </dl>
          {prodConcerns.length > 0 ? (
            <div className="hirelane-eval-panel__roleContextSub">
              <div className="hirelane-eval-panel__fitLabel">Production experience concerns</div>
              <ul className="hirelane-eval-panel__fitList">
                {prodConcerns.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      {far && typeof far === 'object' && (far.decision || far.reason) ? (
        <div className="hirelane-eval-panel__roleContextBlock hirelane-eval-panel__roleContextBlock--advertised">
          <div className="hirelane-eval-panel__fitLabel">Fit for advertised role</div>
          <div className="hirelane-eval-panel__roleContextRow hirelane-eval-panel__roleContextRow--afterLabel">
            {far.decision ? <span className={advertisedFitBadgeClass(far.decision)}>{far.decision}</span> : null}
          </div>
          {far.reason ? <p className="hirelane-eval-panel__roleContextProse">{far.reason}</p> : null}
        </div>
      ) : null}

      {alt && typeof alt === 'object' && (alt.is_suitable_for_alternate_role || roles.length || alt.reason) ? (
        <div className="hirelane-eval-panel__roleContextBlock hirelane-eval-panel__roleContextBlock--alternate">
          <div className="hirelane-eval-panel__fitLabel">Alternate role fit</div>
          <p className="hirelane-eval-panel__roleContextMeta">
            {alt.is_suitable_for_alternate_role ? 'Yes — consider adjacent roles' : 'Not highlighted for alternate roles'}
          </p>
          {roles.length > 0 ? (
            <div className="hirelane-eval-panel__pillRow hirelane-eval-panel__pillRow--spaced">
              {roles.map((x, i) => (
                <span key={i} className="hirelane-eval-panel__pill hirelane-eval-panel__pill--nice">
                  {x}
                </span>
              ))}
            </div>
          ) : null}
          {alt.reason ? <p className="hirelane-eval-panel__roleContextProse">{alt.reason}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

function PrioritizedGapsSection({ critical, important, preferred }) {
  const crit = Array.isArray(critical) ? critical : [];
  const imp = Array.isArray(important) ? important : [];
  const pref = Array.isArray(preferred) ? preferred : [];
  const hasAny =
    crit.length > 0 ||
    imp.some((x) => x != null && String(x).trim()) ||
    pref.some((x) => x != null && String(x).trim());
  if (!hasAny) return null;

  return (
    <section className="hirelane-eval-panel__gaps">
      <h3 className="hirelane-eval-panel__heading">Prioritized requirement gaps</h3>
      <p className="hirelane-eval-panel__gapsHint">
        Critical items affect day-one execution; summaries above should reflect these same gaps.
      </p>

      {crit.length > 0 ? (
        <div className="hirelane-eval-panel__gapsBlock">
          <div className="hirelane-eval-panel__fitLabel">Critical missing requirements</div>
          <div className="hirelane-eval-panel__gapCardGrid">
            {crit.map((item, idx) => {
              const req =
                item && typeof item === 'object'
                  ? item.requirement || `Gap ${idx + 1}`
                  : String(item || '');
              const why =
                item && typeof item === 'object' ? item.reason_it_matters || '' : '';
              const impact =
                item && typeof item === 'object' ? item.business_impact || '' : '';
              if (!req && !why && !impact) return null;
              return (
                <div key={idx} className="hirelane-eval-panel__gapCard">
                  <div className="hirelane-eval-panel__gapCardTitle">{req || 'Requirement'}</div>
                  {why ? (
                    <p className="hirelane-eval-panel__gapCardLine">
                      <span className="hirelane-eval-panel__gapCardLabel">Why it matters</span>
                      {why}
                    </p>
                  ) : null}
                  {impact ? (
                    <p className="hirelane-eval-panel__gapCardLine">
                      <span className="hirelane-eval-panel__gapCardLabel">Business impact</span>
                      {impact}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {imp.length > 0 ? (
        <div className="hirelane-eval-panel__gapsBlock">
          <div className="hirelane-eval-panel__fitLabel">Important missing requirements</div>
          <ul className="hirelane-eval-panel__fitList">
            {imp.map((x, i) => (
              <li key={i}>{String(x)}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {pref.length > 0 ? (
        <div className="hirelane-eval-panel__gapsBlock">
          <div className="hirelane-eval-panel__fitLabel">Preferred gaps</div>
          <ul className="hirelane-eval-panel__fitList hirelane-eval-panel__fitList--muted">
            {pref.map((x, i) => (
              <li key={i}>{String(x)}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function FitScoreCard({ title, block }) {
  if (!block || typeof block !== 'object') return null;
  const score = block.score != null ? Number(block.score) : null;
  const assessment = block.assessment || '';
  const evidence = asArray(block.evidence);
  const concerns = asArray(block.concerns);

  return (
    <div className="hirelane-eval-panel__fitCard">
      <div className="hirelane-eval-panel__fitCardHead">
        <span className="hirelane-eval-panel__fitCardTitle">{title}</span>
        {score != null && Number.isFinite(score) && (
          <span className="hirelane-eval-panel__fitScore">{Math.round(score)}</span>
        )}
      </div>
      {assessment ? <p className="hirelane-eval-panel__fitAssessment">{assessment}</p> : null}
      {evidence.length > 0 && (
        <div className="hirelane-eval-panel__fitListBlock">
          <div className="hirelane-eval-panel__fitLabel">Evidence</div>
          <ul className="hirelane-eval-panel__fitList">
            {evidence.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </div>
      )}
      {concerns.length > 0 && (
        <div className="hirelane-eval-panel__fitListBlock hirelane-eval-panel__fitListBlock--concerns">
          <div className="hirelane-eval-panel__fitLabel">Concerns</div>
          <ul className="hirelane-eval-panel__fitList">
            {concerns.map((x, i) => (
              <li key={i}>{x}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TechnicalFitCard({ block }) {
  if (!block || typeof block !== 'object') return null;
  const score = block.score != null ? Number(block.score) : null;
  const mm = asArray(block.matched_must_have_skills);
  const mmis = asArray(block.missing_must_have_skills);
  const gm = asArray(block.matched_good_to_have_skills);
  const gmis = asArray(block.missing_good_to_have_skills);
  return (
    <div className="hirelane-eval-panel__fitCard hirelane-eval-panel__fitCard--wide">
      <div className="hirelane-eval-panel__fitCardHead">
        <span className="hirelane-eval-panel__fitCardTitle">Technical skill fit</span>
        {score != null && Number.isFinite(score) && (
          <span className="hirelane-eval-panel__fitScore">{Math.round(score)}</span>
        )}
      </div>
      {block.assessment ? <p className="hirelane-eval-panel__fitAssessment">{block.assessment}</p> : null}
      <div className="hirelane-eval-panel__skillGrid">
        <div>
          <div className="hirelane-eval-panel__fitLabel">Must-have matched</div>
          <div className="hirelane-eval-panel__pillRow">
            {mm.length ? mm.map((x, i) => (
              <span key={i} className="hirelane-eval-panel__pill hirelane-eval-panel__pill--ok">
                {x}
              </span>
            )) : (
              <span className="hirelane-eval-panel__muted">—</span>
            )}
          </div>
        </div>
        <div>
          <div className="hirelane-eval-panel__fitLabel">Must-have gaps</div>
          <div className="hirelane-eval-panel__pillRow">
            {mmis.length ? mmis.map((x, i) => (
              <span key={i} className="hirelane-eval-panel__pill hirelane-eval-panel__pill--risk">
                {x}
              </span>
            )) : (
              <span className="hirelane-eval-panel__muted">None called out</span>
            )}
          </div>
        </div>
        <div>
          <div className="hirelane-eval-panel__fitLabel">Good-to-have matched</div>
          <div className="hirelane-eval-panel__pillRow">
            {gm.length ? gm.map((x, i) => (
              <span key={i} className="hirelane-eval-panel__pill hirelane-eval-panel__pill--nice">
                {x}
              </span>
            )) : (
              <span className="hirelane-eval-panel__muted">—</span>
            )}
          </div>
        </div>
        <div>
          <div className="hirelane-eval-panel__fitLabel">Preferred gaps</div>
          <div className="hirelane-eval-panel__pillRow">
            {gmis.length ? gmis.map((x, i) => (
              <span key={i} className="hirelane-eval-panel__pill hirelane-eval-panel__pill--soft">
                {x}
              </span>
            )) : (
              <span className="hirelane-eval-panel__muted">—</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectCards({ projects }) {
  const list = Array.isArray(projects) ? projects : [];
  if (!list.length) return null;
  return (
    <div className="hirelane-eval-panel__projects">
      <h4 className="hirelane-eval-panel__sectionTitle">Project review</h4>
      <div className="hirelane-eval-panel__projectGrid">
        {list.map((p, idx) => (
          <div key={idx} className="hirelane-eval-panel__projectCard">
            <div className="hirelane-eval-panel__projectTitle">{p.project_name || `Project ${idx + 1}`}</div>
            <dl className="hirelane-eval-panel__dl">
              {p.domain ? (
                <>
                  <dt>Domain</dt>
                  <dd>{p.domain}</dd>
                </>
              ) : null}
              {p.duration ? (
                <>
                  <dt>Duration</dt>
                  <dd>{p.duration}</dd>
                </>
              ) : null}
              {p.team_size ? (
                <>
                  <dt>Team size</dt>
                  <dd>{p.team_size}</dd>
                </>
              ) : null}
              {p.candidate_role ? (
                <>
                  <dt>Role</dt>
                  <dd>{p.candidate_role}</dd>
                </>
              ) : null}
            </dl>
            {asArray(p.responsibilities).length > 0 ? (
              <>
                <div className="hirelane-eval-panel__fitLabel">Responsibilities</div>
                <ul className="hirelane-eval-panel__fitList">
                  {asArray(p.responsibilities).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </>
            ) : null}
            {p.complexity_assessment ? (
              <p className="hirelane-eval-panel__projectNote">
                <strong>Complexity:</strong> {p.complexity_assessment}
              </p>
            ) : null}
            {p.architecture_ownership ? (
              <p className="hirelane-eval-panel__projectNote">
                <strong>Architecture ownership:</strong> {p.architecture_ownership}
              </p>
            ) : null}
            {p.relevance_to_jd ? (
              <p className="hirelane-eval-panel__projectNote">
                <strong>JD relevance:</strong> {p.relevance_to_jd}
              </p>
            ) : null}
            {asArray(p.concerns).length > 0 && (
              <div className="hirelane-eval-panel__fitListBlock hirelane-eval-panel__fitListBlock--concerns">
                <div className="hirelane-eval-panel__fitLabel">Concerns</div>
                <ul className="hirelane-eval-panel__fitList">
                  {asArray(p.concerns).map((x, i) => (
                    <li key={i}>{x}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function RiskList({ title, items, variant }) {
  const list = asArray(items);
  if (!list.length) return null;
  return (
    <div className={`hirelane-eval-panel__riskCol hirelane-eval-panel__riskCol--${variant}`}>
      <div className="hirelane-eval-panel__riskTitle">{title}</div>
      <ul className="hirelane-eval-panel__riskList">
        {list.map((x, i) => (
          <li key={i}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Rich Hirelane v2 structured candidate review (Senior HM + Architect). Expects row fields from API or equivalent on raw_ai_response.
 */
export default function CandidateEvaluationPanel({ evaluation, legacyDetail }) {
  const fb =
    evaluation.fit_breakdown && typeof evaluation.fit_breakdown === 'object'
      ? evaluation.fit_breakdown
      : evaluation.raw_ai_response?.fit_breakdown || {};
  const jd =
    evaluation.jd_understanding && typeof evaluation.jd_understanding === 'object'
      ? evaluation.jd_understanding
      : evaluation.raw_ai_response?.jd_understanding || {};
  const risks =
    evaluation.risks && typeof evaluation.risks === 'object'
      ? evaluation.risks
      : {
          critical_risks: evaluation.raw_ai_response?.critical_risks,
          medium_risks: evaluation.raw_ai_response?.medium_risks,
          low_risks: evaluation.raw_ai_response?.low_risks,
          unknowns_to_verify: evaluation.raw_ai_response?.unknowns_to_verify
        };

  const techAreas = asArray(
    evaluation.interview_focus_areas ?? evaluation.raw_ai_response?.technical_interview_focus_areas
  );
  const recruitQs = asArray(
    evaluation.recruiter_screening_questions ?? evaluation.raw_ai_response?.recruiter_screening_questions
  );
  const archQs = asArray(
    evaluation.architect_interview_questions ?? evaluation.raw_ai_response?.architect_interview_questions
  );

  const decision =
    evaluation.recommended_decision || evaluation.raw_ai_response?.recommended_decision || '';
  const confidence = evaluation.decision_confidence || evaluation.raw_ai_response?.decision_confidence || '';
  const matchScore =
    evaluation.overall_match_score != null
      ? evaluation.overall_match_score
      : evaluation.match_score ?? evaluation.raw_ai_response?.overall_match_score;

  const execSummary =
    evaluation.evaluation_summary ||
    evaluation.raw_ai_response?.executive_summary ||
    '';
  const hmView = evaluation.senior_hiring_manager_view || evaluation.raw_ai_response?.senior_hiring_manager_view || '';
  const archView = evaluation.senior_architect_view || evaluation.raw_ai_response?.senior_architect_view || '';
  const finalRec =
    evaluation.final_recommendation_summary || evaluation.raw_ai_response?.final_recommendation_summary || '';

  const projDepth = fb.project_depth_fit || {};
  const missingProjInfo = asArray(projDepth.missing_project_information);

  const cer =
    evaluation.core_role_execution_risk && typeof evaluation.core_role_execution_risk === 'object'
      ? evaluation.core_role_execution_risk
      : evaluation.raw_ai_response?.core_role_execution_risk;
  const far =
    evaluation.fit_for_advertised_role && typeof evaluation.fit_for_advertised_role === 'object'
      ? evaluation.fit_for_advertised_role
      : evaluation.raw_ai_response?.fit_for_advertised_role;
  const alt =
    evaluation.alternate_role_fit && typeof evaluation.alternate_role_fit === 'object'
      ? evaluation.alternate_role_fit
      : evaluation.raw_ai_response?.alternate_role_fit;

  const criticalGaps =
    evaluation.critical_missing_requirements ?? evaluation.raw_ai_response?.critical_missing_requirements;
  const importantGaps =
    evaluation.important_missing_requirements ?? evaluation.raw_ai_response?.important_missing_requirements;
  const preferredGapsList =
    evaluation.preferred_gaps ?? evaluation.raw_ai_response?.preferred_gaps;

  return (
    <div className="hirelane-eval-panel">
      <div className="hirelane-eval-panel__quick">
        <div className="hirelane-eval-panel__quickTitle">Cony overview</div>
        <p className="hirelane-eval-panel__quickText">
          Eligibility (prior format): <strong>{evaluation.eligibility}</strong>
          {evaluation.match_score != null ? (
            <>
              {' '}
              · Cony match score (legacy compat): <strong>{evaluation.match_score}</strong>
            </>
          ) : null}
        </p>
      </div>

      <section className="hirelane-eval-panel__final">
        <h3 className="hirelane-eval-panel__heading">Cony recommendation</h3>
        <div className="hirelane-eval-panel__finalRow">
          {decision ? <span className={decisionBadgeClass(decision)}>{decision}</span> : null}
          {matchScore != null && matchScore !== '' ? (
            <span className="hirelane-eval-panel__metaPill">Cony match {matchScore}</span>
          ) : null}
          {confidence ? <span className="hirelane-eval-panel__metaPill">Confidence {confidence}</span> : null}
        </div>
        {finalRec ? <p className="hirelane-eval-panel__finalSummary">{finalRec}</p> : null}
      </section>

      <CoreRoleExecutionSection cer={cer} far={far} alt={alt} />

      <PrioritizedGapsSection
        critical={criticalGaps}
        important={importantGaps}
        preferred={preferredGapsList}
      />

      {execSummary ? (
        <details className="hirelane-eval-panel__details">
          <summary>Cony executive summary</summary>
          <p className="hirelane-eval-panel__prose">{execSummary}</p>
        </details>
      ) : null}

      {hmView ? (
        <details className="hirelane-eval-panel__details">
          <summary>Senior hiring manager view</summary>
          <p className="hirelane-eval-panel__prose">{hmView}</p>
        </details>
      ) : null}

      {archView ? (
        <details className="hirelane-eval-panel__details">
          <summary>Senior architect view</summary>
          <p className="hirelane-eval-panel__prose">{archView}</p>
        </details>
      ) : null}

      <details className="hirelane-eval-panel__details">
        <summary>JD understanding (extracted)</summary>
        <div className="hirelane-eval-panel__jdGrid">
          <div>
            <span className="hirelane-eval-panel__fitLabel">Role level</span>
            <p>{jd.role_level || '—'}</p>
          </div>
          <div>
            <span className="hirelane-eval-panel__fitLabel">Experience expectation</span>
            <p>{jd.experience_expectation || '—'}</p>
          </div>
          <div>
            <span className="hirelane-eval-panel__fitLabel">Domain</span>
            <p>{jd.domain_expectation || '—'}</p>
          </div>
          <div>
            <span className="hirelane-eval-panel__fitLabel">Architecture / leadership bar</span>
            <p>{jd.architecture_expectation || '—'}</p>
          </div>
          <div>
            <span className="hirelane-eval-panel__fitLabel">Location</span>
            <p>{jd.location_expectation || '—'}</p>
          </div>
          <div>
            <span className="hirelane-eval-panel__fitLabel">Work mode</span>
            <p>{jd.work_mode_expectation || '—'}</p>
          </div>
        </div>
        {asArray(jd.interview_critical_success_factors).length > 0 && (
          <div className="hirelane-eval-panel__missingProj" style={{ marginTop: 12, background: '#f8fafc' }}>
            <div className="hirelane-eval-panel__fitLabel">Interview-critical success factors (from JD)</div>
            <ul className="hirelane-eval-panel__fitList">
              {asArray(jd.interview_critical_success_factors).map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>
        )}
        <div className="hirelane-eval-panel__skillRows">
          <div>
            <div className="hirelane-eval-panel__fitLabel">Must-have skills</div>
            <div className="hirelane-eval-panel__pillRow">
              {asArray(jd.must_have_skills).length ? (
                asArray(jd.must_have_skills).map((x, i) => (
                  <span key={i} className="hirelane-eval-panel__pill hirelane-eval-panel__pill--must">
                    {x}
                  </span>
                ))
              ) : (
                <span className="hirelane-eval-panel__muted">—</span>
              )}
            </div>
          </div>
          <div>
            <div className="hirelane-eval-panel__fitLabel">Good-to-have skills</div>
            <div className="hirelane-eval-panel__pillRow">
              {asArray(jd.good_to_have_skills).length ? (
                asArray(jd.good_to_have_skills).map((x, i) => (
                  <span key={i} className="hirelane-eval-panel__pill hirelane-eval-panel__pill--nice">
                    {x}
                  </span>
                ))
              ) : (
                <span className="hirelane-eval-panel__muted">—</span>
              )}
            </div>
          </div>
        </div>
      </details>

      <section className="hirelane-eval-panel__fitSection">
        <h3 className="hirelane-eval-panel__heading">Fit breakdown</h3>
        <div className="hirelane-eval-panel__fitGrid">
          <FitScoreCard title="Experience fit" block={fb.experience_fit} />
          <TechnicalFitCard block={fb.technical_skill_fit} />
          <FitScoreCard title="Project depth fit" block={projDepth} />
          <FitScoreCard title="Domain fit" block={fb.domain_fit} />
          <FitScoreCard title="Leadership / architecture fit" block={fb.leadership_architecture_fit} />
          <FitScoreCard title="Location / practical fit" block={fb.location_practical_fit} />
        </div>
        {projDepth.assessment ? (
          <p className="hirelane-eval-panel__projDepthSummary">
            <strong>Projects overview:</strong> {projDepth.assessment}
          </p>
        ) : null}
        {missingProjInfo.length > 0 && (
          <div className="hirelane-eval-panel__missingProj">
            <div className="hirelane-eval-panel__fitLabel">Missing project information</div>
            <ul className="hirelane-eval-panel__fitList">
              {missingProjInfo.map((x, i) => (
                <li key={i}>{x}</li>
              ))}
            </ul>
          </div>
        )}
      </section>

      <ProjectCards projects={projDepth.projects_reviewed} />

      <section className="hirelane-eval-panel__risks">
        <h3 className="hirelane-eval-panel__heading">Risks and unknowns</h3>
        <div className="hirelane-eval-panel__riskGrid">
          <RiskList title="Critical risks" items={risks.critical_risks} variant="critical" />
          <RiskList title="Medium risks" items={risks.medium_risks} variant="medium" />
          <RiskList title="Low risks" items={risks.low_risks} variant="low" />
          <RiskList title="Unknowns to verify" items={risks.unknowns_to_verify} variant="unknown" />
        </div>
      </section>

      <section className="hirelane-eval-panel__interview">
        <h3 className="hirelane-eval-panel__heading">Interview guidance</h3>
        <div className="hirelane-eval-panel__interviewCols">
          <div>
            <div className="hirelane-eval-panel__fitLabel">Technical interview focus</div>
            {techAreas.length ? (
              <ul className="hirelane-eval-panel__fitList">
                {techAreas.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            ) : (
              <p className="hirelane-eval-panel__muted">—</p>
            )}
          </div>
          <div>
            <div className="hirelane-eval-panel__fitLabel">Recruiter screening questions</div>
            {recruitQs.length ? (
              <ul className="hirelane-eval-panel__fitList">
                {recruitQs.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            ) : (
              <p className="hirelane-eval-panel__muted">—</p>
            )}
          </div>
          <div>
            <div className="hirelane-eval-panel__fitLabel">Architect interview questions</div>
            {archQs.length ? (
              <ul className="hirelane-eval-panel__fitList">
                {archQs.map((x, i) => (
                  <li key={i}>{x}</li>
                ))}
              </ul>
            ) : (
              <p className="hirelane-eval-panel__muted">—</p>
            )}
          </div>
        </div>
      </section>

      {legacyDetail ? (
        <details className="hirelane-eval-panel__details hirelane-eval-panel__details--legacy">
          <summary>Earlier summary layout (same data, prior format)</summary>
          <div className="hirelane-eval-panel__legacyInner">{legacyDetail}</div>
        </details>
      ) : null}
    </div>
  );
}
